import clsx from 'clsx'
import {FormikProps, getIn} from 'formik'
import {isEmpty} from 'lodash'
import React, {useEffect, useRef, useState} from 'react'
import {useIntl} from 'react-intl'
import {DS_SEPARATOR, INVALID_COLOR, VALID_COLOR} from '../../../../../_gori/constants'
import {KTSVG, toAbsoluteUrl} from '../../../../../_gori/helpers'

type Props = {
  options: any
  textLabel?: string
  labelClassName?: any
  required?: boolean
  formik: FormikProps<any>
  name: string
  className?: any
  disabled?: boolean
}

type ContentTypeOption = {
  label: string | undefined
  value: string | undefined
}

const ContentType: React.FC<Props> = ({
  options,
  textLabel,
  labelClassName,
  required,
  formik,
  name,
  className,
  disabled = false,
}) => {
  const storeRef = useRef<any>(null)
  const initialContentRef = useRef(formik.values.international_option?.content)
  const initialOptionsRef = useRef(options)
  const intl = useIntl()
  const [visibleMenu, setVisibleMenu] = useState(false)
  const [visibleSubMenu, setVisibleSubMenu] = useState<string | null>(null)
  const [currentContentType, setCurrentContentType] = useState<ContentTypeOption>({
    label: undefined,
    value: undefined,
  })

  let fieldProps = formik.getFieldProps(name)
  let formikErrors = getIn(formik.errors, name)
  let formikTouched = getIn(formik.touched, name)
  let isInvalid = formikTouched && formikErrors
  let isValid = formikTouched && !formikErrors && fieldProps.value

  const customStyle = {
    borderColor: isValid ? VALID_COLOR : isInvalid ? INVALID_COLOR : DS_SEPARATOR,
    borderWidth: '1px',
    borderStyle: 'solid',
  }

  const toggleMenu = (toggle: boolean | null = null) => {
    setVisibleMenu((pre) => {
      if (toggle !== null) {
        return toggle
      } else {
        return !pre
      }
    })
  }

  const handleDropdownClick = () => {
    formik.setFieldTouched(name, true)
    toggleMenu()
  }

  useEffect(() => {
    function findContentType(value) {
      for (const category of initialOptionsRef.current) {
        const match = category.options.find((opt) => opt.value === value)
        if (match) {
          return {
            category: category.label,
            contentType: match,
          }
        }
      }
      return null
    }
    // Handle initial content type value
    const initOption = findContentType(initialContentRef.current)
    const initContentType = initOption?.contentType
    const initCategory = initOption?.category

    if (initContentType) {
      setCurrentContentType(initContentType)
    }

    if (initCategory) {
      setVisibleSubMenu(initCategory)
    }
    const handleClickOutside = async (event) => {
      if (storeRef.current && !storeRef.current.contains(event.target)) {
        toggleMenu(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const renderMenuItem = (item, index) => {
    const {label, options} = item

    const handleClickMenuItem = () => {
      formik.setFieldTouched(name, true)
      setVisibleSubMenu(label)
    }

    const handleClickSubmenuItem = async (subItem) => {
      toggleMenu(false)
      setCurrentContentType(subItem)
      await formik.setFieldValue(name, subItem.value)
    }

    return (
      <div className={clsx('dropdown-submenu', {show: visibleSubMenu === label})} key={index}>
        <div
          className={clsx('dropdown-submenu__item item-custom', {
            show: visibleSubMenu === label,
          })}
          onClick={() => handleClickMenuItem()}
        >
          {intl.formatMessage({id: label})}
        </div>
        <div
          className={clsx('dropdown-submenu__inner', {show: visibleSubMenu === label})}
          style={{overflowY: 'auto', maxHeight: '200px'}}
        >
          {!isEmpty(options) &&
            options?.map((subItem) => {
              return (
                <div
                  key={subItem.label}
                  className={clsx('dropdown-submenu__inner__item item-custom', {
                    'text-white bg-primary': subItem.value === fieldProps.value,
                    'text-hover-primary bg-hover-active-primary':
                      subItem.value !== fieldProps.value,
                  })}
                  onClick={() => handleClickSubmenuItem(subItem)}
                >
                  {intl.formatMessage({id: subItem.label})}
                </div>
              )
            })}
        </div>
      </div>
    )
  }

  return (
    <>
      {textLabel && (
        <label className={`form-label ${labelClassName} ${required ? 'required' : ''}`}>
          {textLabel}
        </label>
      )}
      <div className={clsx('dropdown', className, {'cursor-no-drop': disabled})} ref={storeRef}>
        <div
          className={clsx('dropdown__label dropdown-trigger', {'pe-none': disabled})}
          onClick={handleDropdownClick}
          style={customStyle}
        >
          <div className='dropdown__label__left fw-bold'>
            <div>
              {currentContentType.label ? intl.formatMessage({id: currentContentType.label}) : ''}
            </div>
          </div>
          <div className='dropdown__label__arrow dropdown-trigger__indicator'>
            <span className='dropdown-trigger__separator' />
            <KTSVG
              path='/media/gori/arrows/chevron_down.svg'
              className='dropdown-trigger__chevron'
            />
          </div>
        </div>
        <div
          className={clsx('dropdown-menu dropdown__menu  mw-100', {show: visibleMenu})}
          style={{width: '100%'}}
        >
          {options.map((item, index) => renderMenuItem(item, index))}
        </div>
        {formikTouched && formikErrors && (
          <div className={clsx('fv-plugins-message-container d-flex gap-1')}>
            <img alt='err' src={toAbsoluteUrl('/media/gori/invalid.svg')} width={16} height={20} />
            <div className='fv-help-block text-danger'>
              {typeof formikErrors === 'object' ? (
                Object.values(formikErrors).map((errorField: any, index) => (
                  <>
                    <span role='alert' key={index}>
                      {errorField}
                    </span>
                    <br />
                  </>
                ))
              ) : (
                <span role='alert'>{formikErrors}</span>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  )
}

export {ContentType}
